/**
 * ReportView — the client's free trademark report, rendered in the Payments
 * section above the quote/pay card.
 *
 * Owns its own useReportQuery so report fetches/refetches never re-render
 * PaymentsSection (whose Embedded Checkout must not remount mid-session) —
 * it is rendered as a SIBLING of the payment cards, never as a wrapper.
 * Absence or failure of the report renders nothing: the payment UI is
 * completely unaffected either way.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { useReportQuery } from '../../../hooks/queries/report';

// Section order mirrors the emailed report (reportsRepository shape)
const SECTION_KEYS = [
  'overview',
  'markAssessment',
  'registerFindings',
  'coverageRationale',
  'ourRecommendation',
  'nextSteps',
];

const formatClasses = (classes) => {
  if (!Array.isArray(classes) || classes.length === 0) return '—';
  return classes.join(', ');
};

const ReportView = ({ applicationId }) => {
  const { t } = useTranslation('dashboard');
  const { data: report } = useReportQuery(applicationId);

  if (!report) return null;

  const sections = report.sections || {};
  const sectionEntries = SECTION_KEYS
    .map((key) => ({ key, text: sections[key] }))
    .filter(({ text }) => typeof text === 'string' && text.trim().length > 0);

  const matches = [
    ...(report.registerSearch?.exactMatches || []),
    ...(report.registerSearch?.nearMatches || []),
  ];

  if (sectionEntries.length === 0 && matches.length === 0) return null;

  const reportDate = report.sentAt || report.generatedAt;

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
            <FileText className="h-5 w-5 text-[#00796b]" />
          </div>
          <div>
            <CardTitle>{t('payments.report.title', 'Your trademark report')}</CardTitle>
            <CardDescription>
              {reportDate
                ? t('payments.report.preparedOn', 'Prepared by our team on {{date}}', {
                    date: new Date(reportDate).toLocaleDateString(),
                  })
                : t('payments.report.subtitle', 'Prepared by our team')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {sectionEntries.map(({ key, text }) => (
            <div key={key}>
              <H3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500">
                {t(`payments.report.sections.${key}`)}
              </H3>
              <Text className="text-gray-700 whitespace-pre-line">{text}</Text>
            </div>
          ))}

          {matches.length > 0 && (
            <div>
              <H3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                {t('payments.report.matchesTitle', 'IP Australia register results')}
              </H3>
              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <th className="px-3 py-2 font-medium">{t('payments.report.matchName', 'Mark')}</th>
                      <th className="px-3 py-2 font-medium">{t('payments.report.matchNumber', 'Number')}</th>
                      <th className="px-3 py-2 font-medium">{t('payments.report.matchStatus', 'Status')}</th>
                      <th className="px-3 py-2 font-medium">{t('payments.report.matchClasses', 'Classes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((match, index) => (
                      <tr key={`${match.number || match.name || 'match'}-${index}`} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-900">{match.name || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{match.number || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{match.status || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{formatClasses(match.classes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportView;
